import { ArrowBackIosNewRounded } from "@mui/icons-material";
import { Box, Button, Stack, useTheme } from "@mui/material";
import { useState } from "react";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../components/common/ShowToast";
import apiService from "../../../../../service/Api";
import { tokens } from "../../../../../theme";
import { CoverPageTemplate } from "../../types/cover.types";
import ClientDetailsSection from "./components/create-proposal/ClientDetailsSection";
import CoverPagesSection from "./components/create-proposal/CoverPageSection";
import { MappedPackage } from "./components/create-proposal/hooks/UsePackages";
import { ProposalService } from "./components/create-proposal/hooks/UseServices";
import IntroductionLetterSection from "./components/create-proposal/IntroductionLetterSection";
import PackageSection from "./components/create-proposal/PackageSection";
import PreviousStreamsSection from "./components/create-proposal/PreviousStreams";
import IncludedServicesSection from "./components/create-proposal/ServicesSection";
import SmartActionsSidebar from "./components/create-proposal/SmartActionsSideBar";
import TermsAndConditionsSection from "./components/create-proposal/TermsAndConditions";
import TestimonialsSection from "./components/create-proposal/Testimonials";

const getDefaultTerms = (serviceType?: string) => {
  const type = serviceType?.toUpperCase();

  if (type === "CONFERENCE") {
    return "Payment terms: 40% deposit required, balance due 21 days before the conference. All services are subject to availability and confirmation.";
  }

  if (type === "EVENT") {
    return "Payment terms: 30% deposit required, balance due 14 days before the event. All services are subject to availability.";
  }

  return "Payment terms: 50% deposit required, balance due within 30 days. All services are subject to availability and final confirmation.";
};

interface CreateProposalPageProps {
  defaultValues?: {
    proposalId?: number;
    clientName?: string;
    email?: string;
    phone?: string;
    address?: string;
    deceasedName?: string;
    packageName?: string;
    serviceType?: string;
    eventTitle?: string;
    serviceDate?: string;
    location?: string;
    bookingId?: number;
     venueId?: number | null;
    defaultServices?: ProposalService[];
    introductionLetter?: string;
    termsAndConditions?: string;
    previousStreams?: { id?: string; title: string; url: string }[];
  };
  onTemplateChange?: (template: CoverPageTemplate | null) => void;
  onSave?: () => void;
  onSendToClient?: () => void;
  onFinishLater?: () => void;
  onStreamsChange?: (
    streams: { id?: string; title: string; url: string }[],
  ) => void;
   onBack?: () => void;
}
export default function CreateProposalPage({
  defaultValues,
  onTemplateChange,
  onSave,
  onSendToClient,
  onFinishLater,
  onStreamsChange,
  onBack,
}: CreateProposalPageProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [clientDetails, setClientDetails] = useState({
    clientName: defaultValues?.clientName ?? "",
    email: defaultValues?.email ?? "",
    phone: defaultValues?.phone ?? "",
    address: defaultValues?.address ?? "",
    deceasedName: defaultValues?.deceasedName ?? "",
  });
  const [introLetter, setIntroLetter] = useState(
    defaultValues?.introductionLetter ?? "",
  );
  const [selectedPackage, setSelectedPackage] = useState<MappedPackage | null>(
    null,
  );
  const [services, setServices] = useState<ProposalService[]>([]);
  const [termsAndConditions, setTermsAndConditions] = useState(
    defaultValues?.termsAndConditions ||
      getDefaultTerms(defaultValues?.serviceType),
  );
  const [selectedTestimonialIds, setSelectedTestimonialIds] = useState<
    number[]
  >([]);
  const [saving, setSaving] = useState(false);

  const handleStreamsChange = (
    streams: { id?: string; title: string; url: string }[],
  ) => {
    setPreviousStreams(streams);
    onStreamsChange?.(streams);
  };
  const [previousStreams, setPreviousStreams] = useState(
    defaultValues?.previousStreams ?? [],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        clientName: clientDetails.clientName,
        email: clientDetails.email,
        phone: clientDetails.phone,
        address: clientDetails.address,
        deceasedName: clientDetails.deceasedName,
        package_id: selectedPackage?.id,
        service_ids: services.map((s) => s.service.id),
        introduction_letter: introLetter,
        testimonials: selectedTestimonialIds,
        booking_id: defaultValues?.bookingId,
          venue_id: defaultValues?.venueId,
        status: "draft",
        show_previous_booking_links: previousStreams.length > 0,
        links: previousStreams,
        terms_and_conditions: termsAndConditions,
      };

      if (defaultValues?.proposalId) {
        await apiService.patch(
          `/api/bookings/booking-proposal/${defaultValues.proposalId}/`,
          payload,
        );
      } else {
        await apiService.post("/api/bookings/booking-proposal/", payload);
      }

      showSuccessToast(
        defaultValues?.proposalId ? "Proposal updated!" : "Proposal saved!",
      );
      onSave?.();
    } catch {
      showErrorToast("Failed to save proposal.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {/* Banner */}
      {/* <Paper
        sx={{
          bgcolor: "#fff8f2",
          borderRadius: "12px",
          p: 2,
          mb: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: 0,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "18px",
              color: colors.primary[500],
              mb: 0.5,
            }}
          >
            This proposal is auto filled based on our best package edit to
            customize
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "12px",
              color: colors.primary[500],
              lineHeight: 1.5,
            }}
          >
            To gather client feedback, it is important to create a thoughtful
            review request. Reach out after completing a project or service,
            thank them for their business, and invite them to share their
            experiences.
          </Typography>
        </Box>
        <Button
          sx={{
            bgcolor: colors.primary[500],
            color: "white",
            borderRadius: "8px",
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 500,
            fontSize: "12px",
            minWidth: 97,
            height: 45,
            "&:hover": { bgcolor: colors.primary[600] },
          }}
        >
          Action
        </Button>
      </Paper> */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2.5 }}
      >
        <Button
          onClick={onBack}
          startIcon={<ArrowBackIosNewRounded sx={{ fontSize: 13 }} />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "13px",
            color: colors.primary[100],
            p: 0,
            "&:hover": { bgcolor: "transparent", color: colors.primary[400] },
          }}
        >
          Back to all Proposals
        </Button>
      </Stack>

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        {/*  Form sections */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ClientDetailsSection
            defaultValues={defaultValues}
            onChange={setClientDetails}
          />
          <IntroductionLetterSection
            bookingType={defaultValues?.serviceType}
            proposalData={{
              clientName: defaultValues?.clientName,
              deceasedName: defaultValues?.deceasedName,
              eventTitle: defaultValues?.eventTitle,
              serviceDate: defaultValues?.serviceDate,
              location: defaultValues?.location,
            }}
            onChange={setIntroLetter}
            initialValue={defaultValues?.introductionLetter}
          />
          <PackageSection
            defaultPackageName={defaultValues?.packageName}
            packageType={defaultValues?.serviceType}
            onPackageChange={setSelectedPackage}
          />
          <IncludedServicesSection
            onServicesChange={setServices}
            defaultServices={defaultValues?.defaultServices}
          />
          <PreviousStreamsSection
            streams={previousStreams}
            onStreamsChange={handleStreamsChange}
            venueId={defaultValues?.venueId}
          />
          <TestimonialsSection onChange={setSelectedTestimonialIds} />
          <TermsAndConditionsSection
            onChange={setTermsAndConditions}
            initialValue={
              defaultValues?.termsAndConditions ||
              getDefaultTerms(defaultValues?.serviceType)
            }
          />
          <CoverPagesSection
            bookingType={defaultValues?.serviceType}
            onTemplateSelect={onTemplateChange}
          />
        </Box>

        <SmartActionsSidebar
          onSave={handleSave}
          onSendToClient={onSendToClient}
          onFinishLater={onFinishLater}
          isUpdate={!!defaultValues?.proposalId}
          isSaving={saving}
        />
      </Box>
    </Box>
  );
}
